<template>
	<TheBox :title="'Endgültige Entscheidung:'">
		<template #boxheaderRight>
			<TheButton v-if="isEditing" @buttonclick="stopEditing" type="default">Bearbeitung abbrechen</TheButton>
		</template>

		<TheForm :model="request" :rules="rules" :waiting="waiting" ref="formRef" :id="id" :scrollToError="false">
			<el-form-item prop="state" class="el-row">
				<el-col :sm="12" :xs="24">
					<el-select v-model="request.state" placeholder="Bitte wählen" :disabled="suggestionLoading">
						<el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-col>
			</el-form-item>

			<BoxAiDecision
				:job="aiJob"
				:disabled="!canGenerate"
				:disabledReason="generateDisabledReason"
				@generate="generateDecision"
				class="werberatDecisionForm__ai"
			/>

			<el-row class="flex flex--alignstart werberatDecisionForm__text">
				<el-col :xs="24" :sm="12">
					<h3>Begründung</h3>
				</el-col>

				<el-col :xs="24" :sm="12" class="flex flex--justifyend">
					<el-select
						v-if="suggestions.length > 0"
						v-model="selectedSuggestionId"
						:placeholder="texts.aiDecision.previousSuggestion"
						@change="loadSuggestion"
						class="werberatDecisionForm__suggestions"
					>
						<el-option v-for="item in suggestionOptions" :key="item.value" :label="item.label" :value="item.value" />
					</el-select>
				</el-col>
			</el-row>

			<el-alert v-if="suggestionWarnings.length > 0" type="warning" :closable="false" show-icon class="werberatDecisionForm__warning">
				<template #title>
					<p v-if="!suggestionRuleCheckPassed"><strong>{{ texts.aiDecision.ruleCheckWarning }}</strong></p>
					<ul>
						<li v-for="(warning, index) in suggestionWarnings" :key="index">{{ warning }}</li>
					</ul>
				</template>
			</el-alert>

			<el-form-item label="" prop="text">
				<el-input
					v-model="request.text"
					:autosize="{ minRows: 10 }"
					type="textarea"
					:disabled="aiRunning"
					placeholder="Schreiben Sie hier Ihre Begründung oder nutzen Sie die KI-Funktion, um einen Vorschlag erstellen zu lassen. Diesen können Sie jederzeit bearbeiten."
				/>
			</el-form-item>

			<el-alert :title="errorMsg" type="error" v-if="error" />

			<TheButton type="danger" @buttonclick="triggerDialog" :disabled="waiting">Entscheidung treffen</TheButton>
		</TheForm>
	</TheBox>

	<el-dialog
		v-model="openDialogConfirmFinalDecision"
		:title="'Endgültige Entscheidung treffen'"
		id="dialogConfirmFinalDecision"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<div class="has-status" :class="`has-status--red`">
			{{ request.state?.label }}
		</div>

		<el-alert type="info" :closable="false" effect="dark" show-icon>
			<template #title>
				<p><strong>Wichtig:</strong></p>
				<p>
					Mit dieser Entscheidung bestätigen Sie, dass Sie gemäß der Mehrheit der Werberäte handeln und die Regeln des Werberats sowie den Ethik-Kodex
					einhalten.
				</p>
			</template>
		</el-alert>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="saveDecision" :disabled="waiting" :loading="waiting">Entscheidung treffen</TheButton>
				<TheButton type="default" @buttonclick="openDialogConfirmFinalDecision = false">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import TheForm from '../TheForm.vue';
import TheButton from '../TheButton.vue';
import TheBox from '../TheBox.vue';
import { texts } from '../../utils/texts';
import { defineComponent, ref } from 'vue';
import { returnFormOptions } from '@/utils/formOptions';
import { validateForm, resetForm, dialogBeforeClose } from '@/utils/functions';
import type { FormInstance } from 'element-plus';
import { sendCase, caseFinalDecision } from '../../requests/cases';
import { CaseState, FinalDecisionRequest, DecisionState } from '../../types/types';
import { prepareSearchRequest } from '@/utils/search/searchUtils';
import BoxAiDecision from './BoxAiDecision.vue';
import { useStore } from '@/store';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getAiDecisionSuggestion, getAiDecisionsForCase } from '@/requests/aiDecisions';
import { AiDecisionSuggestionListItemResponse, AiJobResponse, AiJobStatus } from '../../types/types';

interface BoxWerberatDecisionData {
	request: {
		state: CaseState | null;
		text: string;
	};
	rules: Object;
	id: string;
	waiting: boolean;
	error: boolean;
	errorMsg: string;
	suggestionLoading: boolean;
	DecisionState: typeof DecisionState;
	openDialogConfirmFinalDecision: boolean;
	suggestions: AiDecisionSuggestionListItemResponse[];
	selectedSuggestionId: string | null;
	suggestionWarnings: string[];
	suggestionRuleCheckPassed: boolean;
	startedHere: boolean;
}

export default defineComponent({
	name: 'BoxWerberatDecision',
	data(): BoxWerberatDecisionData {
		return {
			request: {
				state: null,
				text: '',
			},
			rules: {
				state: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				text: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
			},
			id: 'werberatDecisionForm',
			waiting: false,
			error: false,
			errorMsg: texts.cases.saveFailed,
			suggestionLoading: false,
			DecisionState,
			openDialogConfirmFinalDecision: false,
			suggestions: [],
			selectedSuggestionId: null,
			suggestionWarnings: [],
			suggestionRuleCheckPassed: true,
			startedHere: false,
		};
	},
	components: {
		TheBox,
		TheForm,
		TheButton,
		BoxAiDecision,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);
		const store = useStore();

		return {
			formRef,
			store,
		};
	},
	props: {
		caseId: {
			type: String,
			required: true,
		},
		existingDecisions: {
			type: Array,
			default: () => [],
		},
		isEditing: {
			type: Boolean,
			default: false,
		},
		currentFinalDecision: {
			type: Object as () => CaseState | null,
			default: null,
		},
		currentReasonText: {
			type: String,
			default: '',
		},
	},
	emits: ['reload', 'stopEditing'],
	computed: {
		statusOptions() {
			return returnFormOptions('caseStateDecisionsOptions', false, false);
		},
		texts() {
			return texts;
		},
		aiJob(): AiJobResponse | null {
			return this.store.getters['aiDecision/jobForCase'](this.caseId);
		},
		aiRunning(): boolean {
			return this.store.getters['aiDecision/isRunning'](this.caseId);
		},
		/** Die im Dropdown gewaehlte Entscheidungsart, uebersetzt in den Wert, den A3 erwartet. */
		selectedDecisionState(): DecisionState | null {
			const state = (this.request.state as any)?.value as CaseState | undefined;
			switch (state) {
				case CaseState.DecisionNoReason:
					return DecisionState.NoReason;
				case CaseState.DecisionSensitization:
					return DecisionState.Awareness;
				case CaseState.DecisionStop:
					return DecisionState.Stop;
				default:
					return null;
			}
		},
		/** Voten mit Freitext — ohne mindestens einen bricht das Backend in Schritt 1 ohne Modellaufruf ab. */
		reasonTextCount(): number {
			return (this.existingDecisions as any[]).filter((decision) => decision?.reasonText && decision.reasonText.trim().length > 0).length;
		},
		canGenerate(): boolean {
			return !this.aiRunning && this.selectedDecisionState !== null && this.reasonTextCount > 0;
		},
		generateDisabledReason(): string {
			if (this.aiRunning) return texts.aiDecision.disabledRunning;
			if (!this.request.state) return texts.aiDecision.disabledNoDecision;
			if (this.selectedDecisionState === null) return texts.aiDecision.disabledNotSupported;
			if (this.reasonTextCount === 0) return texts.aiDecision.disabledNoReasonTexts;
			return '';
		},
		suggestionOptions() {
			const labels: Record<string, string> = {
				[DecisionState.NoReason]: 'Kein Grund',
				[DecisionState.Awareness]: 'Sensibilisierung',
				[DecisionState.Stop]: 'Stopp',
			};
			return this.suggestions.map((suggestion) => {
				const timestamp = new Date(suggestion.createdAt).toLocaleString('de-AT', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
				});
				const decisionLabel = labels[suggestion.requestedDecision] ?? suggestion.requestedDecision;
				// Ein Vorschlag fuer eine andere Entscheidungsart sagt inhaltlich das Gegenteil —
				// deshalb steht die Art im Label und Abweichungen sind zusaetzlich markiert
				const mismatch = this.selectedDecisionState !== null && suggestion.requestedDecision !== this.selectedDecisionState;
				return {
					value: suggestion.id,
					label: `${timestamp} — ${decisionLabel}${mismatch ? ` (${texts.aiDecision.differentDecision})` : ''}`,
				};
			});
		},
	},
	methods: {
		async triggerDialog() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.openDialogConfirmFinalDecision = true;
		},
		async saveDecision() {
			this.waiting = true;

			let searchRequest = prepareSearchRequest(this.request);

			const requestPreliminaryDecision = {
				caseId: String(this.caseId),
				...searchRequest,
			} as FinalDecisionRequest;

			const result = await caseFinalDecision(requestPreliminaryDecision);
			if (result.success) {
				this.handleSuccess();
			} else {
				this.error = true;
			}

			this.openDialogConfirmFinalDecision = false;
			this.waiting = false;
		},
		handleSuccess() {
			resetForm(this.formRef, this.id);
			this.error = false;
			// Der Fall hat sich gerade geaendert — eine Fehlermeldung zum alten Stand waere irrefuehrend
			this.store.dispatch('aiDecision/discardFailure', this.caseId);

			this.$emit('stopEditing');
			this.$emit('reload');
		},
		async generateDecision() {
			if (!this.canGenerate || this.selectedDecisionState === null) return;

			if (this.request.text.trim().length > 0) {
				try {
					await ElMessageBox.confirm(texts.aiDecision.overwriteConfirm, 'KI-Begründung', {
						confirmButtonText: 'Ersetzen',
						cancelButtonText: 'Abbrechen',
						type: 'warning',
					});
				} catch {
					return;
				}
			}

			this.startedHere = true;
			await this.store.dispatch('aiDecision/start', { caseId: this.caseId, decision: this.selectedDecisionState });
		},

		async loadSuggestions() {
			const result = await getAiDecisionsForCase(this.caseId);
			// Vorschläge der Senatsentscheidung gehören in die Auswahl des Kleinen Senats, nicht hierher
			if (result.success) this.suggestions = result.data.suggestions.filter((suggestion) => suggestion.requestedDecision !== DecisionState.StopSmallSenate);
		},

		async loadSuggestion(suggestionId: string) {
			const result = await getAiDecisionSuggestion(suggestionId);
			if (!result.success) {
				ElMessage({ message: texts.aiDecision.loadFailed, type: 'error' });
				return;
			}
			this.request.text = result.data.text;
			this.suggestionWarnings = result.data.warnings;
			this.suggestionRuleCheckPassed = result.data.ruleCheckPassed;
			this.selectedSuggestionId = suggestionId;
		},

        /** Wechselt die Entscheidungsart, wird das Feld geleert: ein Text der anderen Art sagt inhaltlich das Gegenteil. */
		onDecisionChanged() {
			this.request.text = '';
			this.suggestionWarnings = [];
			this.suggestionRuleCheckPassed = true;
			this.selectedSuggestionId = null;
		},
		stopEditing() {
			this.$emit('stopEditing');
		},
		dialogBeforeClose,
	},
	watch: {
		'request.state'(newValue, oldValue) {
			// Beim initialen Setzen im Editiermodus darf nichts geleert werden
			if (oldValue === null || oldValue === undefined) return;
			if (newValue?.value === oldValue?.value) return;
			this.onDecisionChanged();
		},
		aiJob: {
			deep: true,
			async handler(job: AiJobResponse | null) {
				if (!job || job.status === AiJobStatus.Running) return;
				await this.loadSuggestions();
				if (job.status !== AiJobStatus.Succeeded || !job.suggestionId) return;

				if (this.startedHere) {
					// In dieser Ansicht gestartet und das Ueberschreiben bereits bestaetigt
					await this.loadSuggestion(job.suggestionId);
					this.startedHere = false;
				} else {
					// Fremd gestartet oder Ansicht zwischenzeitlich verlassen: nur bereitstellen,
					// nicht ins Feld springen — dort koennte inzwischen getippter Text stehen
					ElMessage({ message: texts.aiDecision.finished, type: 'success' });
				}
			},
		},
	},
	async mounted() {
		if (this.isEditing) {
			this.request.state = this.statusOptions.find((option) => option.value === this.currentFinalDecision);
			this.request.text = this.currentReasonText;
		}
		await this.loadSuggestions();
		// Haengt sich an einen serverseitig laufenden Job — deckt Reload, Navigation und fremde Laeufe ab
		await this.store.dispatch('aiDecision/attach', this.caseId);
	},
});
</script>

<style lang="scss" scoped>
#werberatDecisionForm {
	.werberatDecisionForm__ai {
		margin-top: 1rem;
	}

	.werberatDecisionForm__suggestions {
		max-width: 320px;
	}

	.werberatDecisionForm__warning {
		margin-bottom: 1rem;

		ul {
			margin: 0.25rem 0 0;
			padding-left: 1.25rem;
		}
	}

	.werberatDecisionForm__text {
		margin-top: 2rem;

		h3 {
			margin: 0;
		}
	}

	.el-button.is-round.is-loading:disabled {
		opacity: 1;
	}

	.el-textarea {
		margin-top: 1rem;
	}
}

.has-status {
	margin-bottom: 1.5rem;
}

#dialogConfirmFinalDecision {
	.el-alert {
		background-color: var(--el-color-lightgrey-3);
	}
}
</style>
